if (!customElements.get('glimtiq-discovery')) {
  customElements.define('glimtiq-discovery', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-discovery-labels]').textContent);
      this.seen = new Set([this.dataset.current]);
      this.busy = false;
      this.feeds = [...this.querySelectorAll('[data-discovery-feed]')].map((el) => ({
        el, grid: el.querySelector('.gd-grid'), button: el.querySelector('button.gd-more'),
        status: el.querySelector('.gd-status'), sentinel: el.querySelector('.gd-sentinel'), end: el.querySelector('.gd-end'),
        seen: this.seen,
        next: el.dataset.url, buffer: [], started: false, done: !el.dataset.url, failed: false,
        auto: el.dataset.auto === 'true'
      }));
      this.feeds.forEach((feed) => {
        const initial = [...feed.grid.querySelectorAll('[data-product-id]')];
        const setting = matchMedia('(min-width: 990px)').matches ? '--gd-columns' : '--gd-mobile';
        const batch = (parseInt(this.style.getPropertyValue(setting), 10) || 2) * 2;
        initial.forEach((item, index) => {
          item.removeAttribute('data-seed-mobile-overflow');
          if (index < batch) feed.seen.add(item.dataset.productId);
          else { item.remove(); feed.buffer.push(item); }
        });
        feed.started = initial.length > 0;
        feed.button.addEventListener('click', () => this.load(feed), { signal: this.abort.signal });
        if (feed.done) this.finish(feed);
      });
      this.setAttribute('data-ready', '');
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.check(); });
      };
      document.addEventListener('visibilitychange', schedule, { signal: this.abort.signal });
      window.addEventListener('scroll', schedule, { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.check(), { rootMargin: '1500px 0px' });
        this.feeds.forEach((feed) => this.observer.observe(feed.sentinel));
      }
      schedule();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); if (this.frame) cancelAnimationFrame(this.frame); }
    check() {
      if (this.busy || document.hidden || !this.isConnected) return;
      for (const feed of this.feeds) {
        const box = feed.sentinel.getBoundingClientRect();
        const rowHeight = feed.grid.firstElementChild?.getBoundingClientRect().height || 400;
        const ahead = Math.max(innerHeight, rowHeight * 2 + 80);
        const earlierComplete = feed === this.feeds[0] || this.feeds[0].done || !this.feeds[0].auto;
        if (!feed.done && !feed.failed && box.top < innerHeight + ahead && box.bottom > 0 &&
            (!feed.started || (feed.auto && earlierComplete))) {
          this.load(feed); return;
        }
      }
    }
    finish(feed) {
      feed.done = true; feed.button.hidden = true;
      feed.end.hidden = false;
      feed.status.textContent = '';
    }
    async load(feed) {
      if (this.busy || feed.done) return;
      this.busy = true; feed.started = true; feed.failed = false;
      this.feeds.forEach((f) => { f.button.disabled = true; });
      feed.el.setAttribute('aria-busy', 'true'); feed.status.textContent = this.labels.loading;
      const columnSetting = matchMedia('(min-width: 990px)').matches ? '--gd-columns' : '--gd-mobile';
      const columns = Math.max(1, parseInt(this.style.getPropertyValue(columnSetting), 10) || 2);
      const batch = columns * 2;
      let pages = 0;
      try {
        feed.buffer = feed.buffer.filter((item) => !feed.seen.has(item.dataset.productId));
        while (feed.buffer.length < batch && feed.next && pages < 6) {
          const url = new URL(feed.next, location.origin);
          if (url.origin !== location.origin) throw new Error('Invalid feed origin');
          url.searchParams.set('section_id', 'glimtiq-discovery-feed');
          const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Product request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = doc.querySelector('[data-discovery-page]');
          if (!page) throw new Error('Product feed missing');
          const buffered = new Set(feed.buffer.map((item) => item.dataset.productId));
          page.querySelectorAll(':scope > [data-product-id]').forEach((item) => {
            const id = item.dataset.productId;
            if (!feed.seen.has(id) && !buffered.has(id)) { feed.buffer.push(item); buffered.add(id); }
          });
          feed.next = page.dataset.next || ''; pages++;
        }
        const added = Math.min(batch, feed.buffer.length);
        const fragment = document.createDocumentFragment();
        feed.buffer.splice(0, batch).forEach((item) => {
          feed.seen.add(item.dataset.productId); fragment.append(item);
        });
        feed.grid.append(fragment);
        if (!feed.next && !feed.buffer.length) this.finish(feed);
        else {
          feed.status.textContent = '';
          if (pages === 6 && !added) { feed.failed = true; feed.status.textContent = this.labels.continue; }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          feed.failed = true; feed.status.textContent = this.labels.error;
          feed.button.textContent = this.labels.retry;
        }
      } finally {
        this.busy = false; feed.el.removeAttribute('aria-busy');
        this.feeds.forEach((f) => { f.button.disabled = false; });
        if (!feed.failed) feed.button.textContent = this.labels.more;
        if (this.isConnected && !this.abort.signal.aborted) requestAnimationFrame(() => this.check());
      }
    }
  });
}
